import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bug,
  Lightbulb,
  MessageCircle,
  MessageSquarePlus,
  Loader2,
  CheckCircle2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import { supabase } from '@/supabase';
import { useToast } from '@/hooks/use-toast';
import { useOrganization } from '@/contexts/OrganizationContext';

type FeedbackType = 'bug' | 'feature' | 'feedback';

interface FeedbackTypeOption {
  type: FeedbackType;
  icon: React.ElementType;
  labelKey: string;
  descriptionKey: string;
}

const FEEDBACK_TYPES: FeedbackTypeOption[] = [
  {
    type: 'bug',
    icon: Bug,
    labelKey: 'feedback.types.bug',
    descriptionKey: 'feedback.types.bugDescription',
  },
  {
    type: 'feature',
    icon: Lightbulb,
    labelKey: 'feedback.types.feature',
    descriptionKey: 'feedback.types.featureDescription',
  },
  {
    type: 'feedback',
    icon: MessageCircle,
    labelKey: 'feedback.types.feedback',
    descriptionKey: 'feedback.types.feedbackDescription',
  },
];

const TEMPLATES: Record<FeedbackType, { titleKey: string; descriptionKey: string }> = {
  bug: {
    titleKey: 'feedback.templates.bug.title',
    descriptionKey: 'feedback.templates.bug.description',
  },
  feature: {
    titleKey: 'feedback.templates.feature.title',
    descriptionKey: 'feedback.templates.feature.description',
  },
  feedback: {
    titleKey: '',
    descriptionKey: '',
  },
};

function captureContext(): Record<string, string> {
  return {
    url: window.location.href,
    browser: navigator.userAgent,
    screenSize: `${window.innerWidth}x${window.innerHeight}`,
    language: navigator.language,
  };
}

export function FeedbackWidget() {
  const { t } = useTranslation('feedback');
  const { toast } = useToast();
  const { currentOrg } = useOrganization();

  const [open, setOpen] = useState(false);
  const [selectedType, setSelectedType] = useState<FeedbackType>('bug');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [hasUserEdited, setHasUserEdited] = useState(false);

  const applyTemplate = (type: FeedbackType) => {
    const template = TEMPLATES[type];
    if (template.titleKey) {
      setTitle(t(template.titleKey));
    } else {
      setTitle('');
    }
    if (template.descriptionKey) {
      setDescription(t(template.descriptionKey));
    } else {
      setDescription('');
    }
  };

  const handleTypeChange = (type: FeedbackType) => {
    setSelectedType(type);
    if (!hasUserEdited) {
      applyTemplate(type);
    }
  };

  const handleTitleChange = (value: string) => {
    setTitle(value);
    setHasUserEdited(true);
  };

  const handleDescriptionChange = (value: string) => {
    setDescription(value);
    setHasUserEdited(true);
  };

  const resetForm = () => {
    setSelectedType('bug');
    setHasUserEdited(false);
    setIsSuccess(false);
  };

  const handleClose = (isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) {
      setTimeout(resetForm, 300);
    }
  };

  useEffect(() => {
    if (open && !hasUserEdited) {
      applyTemplate(selectedType);
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim() || !description.trim()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        toast({
          title: t('feedback.error'),
          description: t('feedback.errorNotLoggedIn'),
          variant: 'destructive',
        });
        return;
      }

      const { error } = await supabase.from('feedback').insert({
        user_id: user.id,
        organization_id: currentOrg?.id || null,
        type: selectedType,
        title: title.trim(),
        description: description.trim(),
        context: captureContext(),
      });

      if (error) {
        throw error;
      }

      setIsSuccess(true);

      toast({
        title: t('feedback.successTitle'),
        description: t('feedback.successDescription'),
        variant: 'success',
      });

      setTimeout(() => {
        handleClose(false);
      }, 1500);
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      toast({
        title: t('feedback.error'),
        description: t('feedback.errorSubmitting'),
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmit = title.trim().length > 0 && description.trim().length > 0;

  return (
    <Sheet open={open} onOpenChange={handleClose}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-2">
          <MessageSquarePlus className="h-4 w-4" />
          <span className="hidden sm:inline">{t('feedback.buttonLabel')}</span>
        </Button>
      </SheetTrigger>

      <SheetContent className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{t('feedback.title')}</SheetTitle>
          <SheetDescription>{t('feedback.description')}</SheetDescription>
        </SheetHeader>

        {isSuccess ? (
          <div className="flex flex-col items-center justify-center py-12 space-y-4">
            <div className="rounded-full bg-green-100 p-3 dark:bg-green-900">
              <CheckCircle2 className="h-8 w-8 text-green-600 dark:text-green-400" />
            </div>
            <p className="text-center text-muted-foreground">{t('feedback.successDescription')}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6 mt-6">
            <div className="space-y-3">
              <Label>{t('feedback.selectType')}</Label>
              <div className="grid gap-3">
                {FEEDBACK_TYPES.map((option) => {
                  const Icon = option.icon;
                  const isSelected = selectedType === option.type;

                  return (
                    <button
                      key={option.type}
                      type="button"
                      onClick={() => handleTypeChange(option.type)}
                      className={cn(
                        'flex items-start gap-3 rounded-lg border-2 p-3 text-left transition-all',
                        isSelected
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/50 hover:bg-muted/50'
                      )}
                    >
                      <div
                        className={cn(
                          'rounded-md p-2',
                          isSelected ? 'bg-primary text-primary-foreground' : 'bg-muted'
                        )}
                      >
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="flex-1 space-y-1">
                        <p className="font-medium text-sm">{t(option.labelKey)}</p>
                        <p className="text-xs text-muted-foreground">{t(option.descriptionKey)}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="feedback-title">{t('feedback.titleLabel')}</Label>
              <Input
                id="feedback-title"
                value={title}
                onChange={(e) => handleTitleChange(e.target.value)}
                placeholder={t('feedback.titlePlaceholder')}
                maxLength={100}
                disabled={isSubmitting}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="feedback-description">{t('feedback.descriptionLabel')}</Label>
              <Textarea
                id="feedback-description"
                value={description}
                onChange={(e) => handleDescriptionChange(e.target.value)}
                placeholder={t('feedback.descriptionPlaceholder')}
                rows={8}
                disabled={isSubmitting}
              />
            </div>

            <Button type="submit" className="w-full" disabled={!canSubmit || isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t('feedback.submitting')}
                </>
              ) : (
                t('feedback.submit')
              )}
            </Button>
          </form>
        )}
      </SheetContent>
    </Sheet>
  );
}

export default FeedbackWidget;
